import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const ThemeContext = createContext(null);
const PREF_KEY = "hyd_theme_pref"; // 'light' | 'dark' | 'system' | null (follow admin default)

function systemTheme() {
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  } catch {
    return "dark";
  }
}

function resolve(pref, adminDefault) {
  if (pref === "light" || pref === "dark") return pref;
  if (pref === "system") return systemTheme();
  if (adminDefault === "light" || adminDefault === "dark") return adminDefault;
  if (adminDefault === "system") return systemTheme();
  return "light";
}

export function ThemeProvider({ children }) {
  const [pref, setPref] = useState(() => {
    const p = localStorage.getItem(PREF_KEY);
    return ["light", "dark", "system"].includes(p) ? p : null;
  });
  const [adminDefault, setAdminDefault] = useState("dark");
  const [sysTick, setSysTick] = useState(0);

  useEffect(() => {
    api.get("/config").then((r) => {
      const m = r?.data?.theme_mode;
      if (["light", "dark", "system"].includes(m)) setAdminDefault(m);
    }).catch(() => {});
  }, []);

  // Re-resolve when the OS theme changes while we're following the system
  const followsSystem = pref === "system" || (pref == null && adminDefault === "system");
  useEffect(() => {
    if (!followsSystem) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => setSysTick((t) => t + 1);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [followsSystem]);

  const resolved = resolve(pref, adminDefault);

  useEffect(() => {
    const el = document.documentElement;
    if (resolved === "light") el.setAttribute("data-theme", "light");
    else el.removeAttribute("data-theme");
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", resolved === "light" ? "#F8F9FA" : "#000000");
  }, [resolved, sysTick]);

  const setPreference = useCallback((p) => {
    if (["light", "dark", "system"].includes(p)) {
      localStorage.setItem(PREF_KEY, p);
      setPref(p);
    } else {
      localStorage.removeItem(PREF_KEY);
      setPref(null);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ pref: pref ?? "default", resolved, adminDefault, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
